import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children?: React.ReactNode;
  content: string;
  className?: string;
  // Applied to the always-rendered sr-only copy so a trigger can reference it
  // with aria-describedby and keep a stable description whether or not it shows.
  id?: string;
}

// The tooltip bubble is portalled to document.body with fixed positioning so it
// escapes overflow-clipped ancestors (e.g. a data table's overflow-x-auto) and
// is never stacked behind the nav. It shows on hover/focus of the trigger.
export const Tooltip: React.FC<TooltipProps> = ({ children, content, className = '', id }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  useLayoutEffect(() => {
    if (!show || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spacing = 8;
    // Prefer above; drop below when there is not enough room at the top.
    const above = rect.top >= 40 + spacing;
    setPos({
      top: above ? rect.top - spacing : rect.bottom + spacing,
      left: rect.left + rect.width / 2,
      above,
    });
  }, [show]);

  // A fixed-position bubble can't follow scroll/resize, so hide it to avoid drift.
  // Escape is swallowed only while the trigger owns focus, so a parent dialog still closes.
  useEffect(() => {
    if (!show) return;
    const hide = () => setShow(false);
    const hideOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (triggerRef.current?.contains(document.activeElement)) event.stopPropagation();
      setShow(false);
    };
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('keydown', hideOnEscape, true);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('keydown', hideOnEscape, true);
    };
  }, [show]);

  return (
      <span
          ref={triggerRef}
          className="inline-flex items-center"
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          onFocus={() => setShow(true)}
          onBlur={() => setShow(false)}
      >
          {children}
          <span id={id} className="sr-only">
              {content}
          </span>
          {show &&
              pos &&
              createPortal(
                  <div
                      role="tooltip"
                      style={{
                          position: 'fixed',
                          top: pos.top,
                          left: pos.left,
                          transform: pos.above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
                          // Above the shared Modal overlay (z-99999, see ui/modal/Modal.tsx) so a tooltip
                          // portalled from inside a modal is not painted behind it; below the toast layer
                          // (999999, see AppLayout.tsx). Matches the shared Select menu.
                          zIndex: 100000,
                      }}
                      className={`pointer-events-none max-w-xs whitespace-normal rounded bg-mp-tooltip px-2 py-1 text-xs text-mp-tooltip-foreground shadow-lg ${className}`}
                  >
                      {content}
                      {/* Arrow pointing back at the trigger; flips with the placement. */}
                      <span
                          className={`absolute left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-mp-tooltip ${
                              pos.above ? '-bottom-1' : '-top-1'
                          }`}
                      />
                  </div>,
                  document.body
              )}
      </span>
  );
};
